import { useEffect, useMemo, useState } from "react";
import { LiveKitRoom, RoomAudioRenderer, ParticipantTile, useTracks } from "@livekit/components-react";
import { Track, VideoQuality } from "livekit-client";
import { Swords } from "lucide-react";
import { api } from "@/lib/api";

// Iter64 B1 — mirror the main-stage LiveKit options so the peek connection
// benefits from adaptive stream (downscale to the tile's CSS size) and
// dynacast (pause decoders when the tile isn't visible). Without these the
// peek room decodes the source resolution into a 168×120 box, ~10× the GPU
// work needed on mobile, driving the "heat + slow-motion video" symptom.
const PEEK_ROOM_OPTIONS = { adaptiveStream: true, dynacast: true };

/**
 * Iter62 — Battle Media Bridge (Option A).
 *
 * A small opponent-video tile that the host mounts during an active battle.
 * Internally it opens a SECOND LiveKit connection to the opponent's room
 * using a subscribe-only, hidden token minted by
 *   POST /api/battles/{battle_id}/bridge/token
 * The peek room is fully isolated from the caller's own room:
 *   - the caller cannot publish tracks into it
 *   - the caller does NOT appear in the opponent's participant list
 *     (hidden=true), so opponent viewer counts stay correct
 *   - opponent audio is rendered ONLY inside this peek tile, so viewers on
 *     either side never hear both hosts mixed together (no echo)
 *
 * Lifecycle: the tile mounts only when `battleId` + `enabled` are truthy
 * (parent gates on `battle.status === "active"`). Any of battle:end /
 * battle:decline / stream:end causes the parent to flip enabled=false,
 * which triggers a clean disconnect. Nothing here calls the LiveKit server
 * to revoke — the short token TTL is the safety net for a leaked token.
 */
export default function BattlePeekTile({ battleId, enabled, opponentHandle, audioBridgeReady = false }) {
  const [tokenInfo, setTokenInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!enabled || !battleId) {
      setTokenInfo(null);
      setErr("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/battles/${battleId}/bridge/token`);
        if (!cancelled) setTokenInfo(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Peek unavailable.");
      }
    })();
    return () => { cancelled = true; };
  }, [battleId, enabled]);

  if (!enabled || !battleId) return null;
  if (err) {
    return (
      <div
        data-testid="battle-peek-tile-error"
        className="rounded-md border border-rose-500/40 bg-black/60 px-2 py-1 text-[10px] text-rose-300"
      >
        Peek unavailable
      </div>
    );
  }
  if (!tokenInfo?.token) {
    return (
      <div
        data-testid="battle-peek-tile-loading"
        className="rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[10px] text-zinc-400"
      >
        Connecting to opponent…
      </div>
    );
  }

  return (
    <div
      data-testid="battle-peek-tile"
      className="rounded-lg border border-purple-400/50 bg-black/80 overflow-hidden shadow-lg w-[140px] h-[100px] sm:w-[168px] sm:h-[120px] flex flex-col"
    >
      <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-rose-950/80 to-purple-950/80 text-[9px] uppercase tracking-widest text-rose-200">
        <Swords size={9} />
        <span className="truncate">vs @{opponentHandle || "opponent"}</span>
      </div>
      <div className="flex-1 relative bg-black">
        <LiveKitRoom
          // Keying by token guarantees a full teardown on token change.
          key={tokenInfo.token}
          token={tokenInfo.token}
          serverUrl={tokenInfo.server_url}
          options={PEEK_ROOM_OPTIONS}
          connect
          audio={false}
          video={false}
          data-lk-theme="default"
          style={{ height: "100%", width: "100%", background: "#000" }}
        >
          {/* Opponent audio — gated exactly like BattleSplitLayout: when the
              server bridge is delivering opponent audio in the MAIN room,
              playing it again here would double it up. */}
          {!audioBridgeReady && <RoomAudioRenderer />}
          <OpponentTile />
        </LiveKitRoom>
      </div>
    </div>
  );
}

/**
 * Picks the opponent host's camera track from the peek room and renders it
 * via LiveKit's stock ParticipantTile. Peek identities (`battle-peek-*`) are
 * hidden server-side but we skip them defensively; any non-peek publisher
 * left in the room is, by definition, the opponent host or their cohost.
 */
function OpponentTile() {
  const trackRefs = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: true }
  );
  const opp = useMemo(() => {
    return trackRefs.find((t) => {
      const pid = t.participant?.identity || "";
      if (pid.startsWith("battle-peek-")) return false;
      const perms = t.participant?.permissions;
      if (perms && perms.canPublish === false) return false;
      return true;
    });
  }, [trackRefs]);

  if (!opp) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-[10px] text-zinc-500">
        Waiting for opponent…
      </div>
    );
  }
  return (
    <div className="absolute inset-0">
      <OpponentVideo trackRef={opp} />
    </div>
  );
}

/**
 * Iter64 B1 — dedicated low-quality subscription wrapper. LiveKit's simulcast
 * has three layers (LOW/MEDIUM/HIGH); the small peek tile is 168×120 CSS so
 * we explicitly pin to LOW. Combined with adaptiveStream + dynacast on the
 * room this reduces the peek connection's decode cost from full source
 * resolution down to LiveKit's LOW simulcast layer (typically ~180p / low
 * bitrate). Applied to the RemoteVideoTrack as soon as it's subscribed and
 * re-applied on publication changes.
 */
function OpponentVideo({ trackRef }) {
  useEffect(() => {
    const pub = trackRef?.publication;
    if (!pub || !pub.setVideoQuality) return;
    try {
      pub.setVideoQuality(VideoQuality.LOW);
    } catch (err) {
      console.warn("[BattlePeekTile] setVideoQuality failed:", err);
    }
  }, [trackRef?.publication]);
  return <ParticipantTile trackRef={trackRef} disableSpeakingIndicator />;
}
